import React from "react";
import { useNavigate } from "react-router-dom";
import creativeLogo from "./assets/creative.png";
import amyAvatar from "./assets/amy.png";
import ccWave from "./assets/cc-wave.svg";
import "./App.css";

const CC_TEAL = "#44A08D";

function IconStopwatch() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <circle cx="12" cy="13" r="8" stroke={CC_TEAL} strokeWidth="1.8" />
      <path d="M12 9v5l3 2" stroke={CC_TEAL} strokeWidth="1.8" strokeLinecap="round" />
      <path d="M9 3h6" stroke={CC_TEAL} strokeWidth="1.8" strokeLinecap="round" />
      <path d="M12 3v2" stroke={CC_TEAL} strokeWidth="1.8" strokeLinecap="round" />
    </svg>
  );
}

function IconVideo() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <rect x="3" y="6" width="13" height="12" rx="2" stroke={CC_TEAL} strokeWidth="1.8" />
      <path d="M16 10l5-3v10l-5-3" stroke={CC_TEAL} strokeWidth="1.8" strokeLinejoin="round" />
    </svg>
  );
}

function IconBrandMini() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M18 6.5a7 7 0 1 0 0 11"
        stroke={CC_TEAL}
        strokeWidth="2.2"
        strokeLinecap="round"
      />
      <path
        d="M16.5 8.2a4.8 4.8 0 1 0 0 7.6"
        stroke="#1A2834"
        strokeWidth="1.8"
        strokeLinecap="round"
      />
    </svg>
  );
}

function BrandConfetti() {
  const pieces = [
    { id: 1, size: "sm", shape: "hex" },
    { id: 2, size: "md", shape: "dot" },
    { id: 3, size: "lg", shape: "hex" },
    { id: 4, size: "sm", shape: "dot" },
    { id: 5, size: "md", shape: "hex" },
    { id: 6, size: "sm", shape: "dot" },
    { id: 7, size: "lg", shape: "hex" },
    { id: 8, size: "md", shape: "dot" },
    { id: 9, size: "sm", shape: "hex" },
    { id: 10, size: "lg", shape: "dot" },
    { id: 11, size: "md", shape: "hex" },
    { id: 12, size: "sm", shape: "dot" },
  ];

  return (
    <div className="cc-schedule-drops" aria-hidden="true">
      {pieces.map((piece) => (
        <span
          key={piece.id}
          className={`cc-drop cc-drop-${piece.id} cc-drop-${piece.size} cc-drop-${piece.shape}`}
        />
      ))}
    </div>
  );
}

function IconCalendarHero() {
  return (
    <svg width="28" height="28" viewBox="0 0 34 34" fill="none" aria-hidden="true">
      <rect x="5" y="8" width="24" height="21" rx="3" stroke="#fff" strokeWidth="2" />
      <path d="M5 14h24" stroke="#fff" strokeWidth="2" />
      <path d="M12 5v5M22 5v5" stroke="#fff" strokeWidth="2" strokeLinecap="round" />
      <circle cx="13" cy="21" r="1.5" fill="#fff" />
      <circle cx="17" cy="21" r="1.5" fill="#fff" />
    </svg>
  );
}

function IconShieldHero() {
  return (
    <svg width="26" height="26" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M12 2l8 3v6c0 5-3.5 9-8 11-4.5-2-8-6-8-11V5l8-3z"
        stroke="#fff"
        strokeWidth="1.6"
        fill="rgba(255,255,255,0.08)"
      />
      <circle cx="12" cy="11" r="2.2" fill="#fff" />
    </svg>
  );
}

function GoogleIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 18 18" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <path
        fill="#4285F4"
        d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.717v2.258h2.908c1.702-1.567 2.684-3.874 2.684-6.615z"
      />
      <path
        fill="#34A853"
        d="M9 18c2.43 0 4.467-.806 5.956-2.184l-2.908-2.258c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332C2.438 15.983 5.482 18 9 18z"
      />
      <path
        fill="#FBBC05"
        d="M3.964 10.707c-.18-.54-.282-1.117-.282-1.707s.102-1.167.282-1.707V4.951H.957C.348 6.174 0 7.55 0 9s.348 2.826.957 4.049l3.007-2.342z"
      />
      <path
        fill="#EA4335"
        d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.582C13.463.891 11.426 0 9 0 5.482 0 2.438 2.017.957 4.951L3.964 7.293C4.672 5.163 6.656 3.58 9 3.58z"
      />
    </svg>
  );
}

export default function CalendlySchedule() {
  const API_URL = process.env.REACT_APP_API_BASE_URL;
  const navigate = useNavigate();

  const handleGoogleLogin = async () => {
    try {
      let sessionId = localStorage.getItem("session_id");

      if (!sessionId) {
        const response = await fetch(`${API_URL}/api/new-user`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
        });

        if (!response.ok) throw new Error("Failed to create session");

        const data = await response.json();
        sessionId = data.session_id;
        localStorage.setItem("session_id", sessionId);
      }

      localStorage.removeItem(`confirmation_done_${sessionId}`);
      localStorage.removeItem(`booking_summary_${sessionId}`);

      navigate("/login");
    } catch (error) {
      console.error("Login error:", error);
      alert("An error occurred. Please try again.");
    }
  };

  return (
    <div className="calendy-div cc-schedule-page">
      <div className="calendy-grid cc-schedule-card">
        <div className="cc-schedule-left">
          <BrandConfetti />

          <div className="cc-schedule-brand-bar">
            <img src={creativeLogo} alt="Creative Circle" className="cc-schedule-logo cc-schedule-logo-top" />
          </div>

          <div className="cc-schedule-profile">
            <div className="cc-schedule-avatar-wrap">
              <img src={amyAvatar} alt="Amy Banks" className="cc-schedule-avatar" />
              <span className="cc-schedule-status" aria-hidden="true" />
            </div>
            <h3 className="cc-schedule-name">Amy Banks</h3>
            <p className="cc-schedule-title">Talent Acquisition</p>
            <p className="cc-schedule-company">
              <IconBrandMini />
              <span>Creative Circle</span>
            </p>
            <p className="cc-schedule-tagline">Remarkable Talent. Flexible Solutions.</p>
          </div>

          <div className="cc-schedule-divider" />

          <div className="cc-schedule-details">
            <div className="cc-schedule-detail-row">
              <div className="cc-schedule-icon-circle">
                <IconStopwatch />
              </div>
              <div className="cc-schedule-detail-text">
                <strong>30 Minute Meeting</strong>
                <span>30 min</span>
              </div>
            </div>
            <div className="cc-schedule-detail-row">
              <div className="cc-schedule-icon-circle">
                <IconVideo />
              </div>
              <div className="cc-schedule-detail-text">
                <strong>Web Conferencing details</strong>
                <span>Provide upon confirmation</span>
              </div>
            </div>
          </div>

          <div className="cc-schedule-wave-footer">
            <img src={ccWave} alt="" className="cc-schedule-wave" aria-hidden="true" />
            <div className="cc-schedule-footer-links">
              <a href="#cookie-settings">Cookie settings</a>
              <span className="cc-schedule-footer-sep">|</span>
              <a href="#privacy">Privacy Policy</a>
            </div>
          </div>
        </div>

        <div className="cc-schedule-right cc-schedule-hero-panel">
          <div className="cc-schedule-hero-overlay" aria-hidden="true" />

          <div className="cc-schedule-hero">
            <img
              src={creativeLogo}
              alt="Creative Circle"
              className="cc-schedule-hero-brand-logo"
            />

            <div className="cc-schedule-hero-icons">
              <div className="cc-schedule-hero-icon-badge">
                <IconCalendarHero />
              </div>
              <div className="cc-schedule-hero-icon-badge">
                <IconShieldHero />
              </div>
            </div>

            <h2 className="cc-schedule-heading">Let&apos;s get you ready for what&apos;s ahead</h2>
            <p className="cc-schedule-intro">
              Sign in with Google so we can verify your details and ensure your meeting stays secure,
              relevant, and tailored to you.
            </p>

            <button type="button" className="cc-schedule-google-btn" onClick={handleGoogleLogin}>
              <span className="cc-schedule-google-icon-wrap">
                <GoogleIcon />
              </span>
              <span className="cc-schedule-google-label">Sign in with Google</span>
            </button>

            <p className="cc-schedule-privacy-inline">
              We respect your privacy and will never share your information without permission.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
